import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Select, SelectOptionType, useToast } from '@aidkitorg/component-library';
import { usePost } from '../../../../API';
import { langToWord, supportedLanguages, useLocalizedStrings } from '../../../../Localization';
import { SupportedLanguage } from '../../../../Context';

export interface ToolsTabProps {
  supportCaseId: string;
  lang?: SupportedLanguage;
  verified?: string[];
}

export const ToolsTab: FC<ToolsTabProps> = ({ supportCaseId, lang: initialLang, verified }) => {
  const L = useLocalizedStrings();
  const [email, setEmail] = useState('');
  const sendLinkVerificationAPI = usePost('/confirm/send_link');
  const { toast } = useToast();

  // Language options with proper typing
  const languageOptions: SelectOptionType[] = Object.entries(supportedLanguages).map(([key, value]) => ({
    label: `${value}${key !== 'en' ? ' (' + langToWord(key as SupportedLanguage, 'Name') + ')' : ''}`,
    value: key,
  }));

  // Find the initial language option
  const initialLangOption = languageOptions.find(opt => opt.value === (initialLang || 'en')) || languageOptions[0];
  const [selectedLang, setSelectedLang] = useState<SelectOptionType>(initialLangOption);

  // Send verification link mutation
  const sendVerificationMutation = useMutation({
    mutationFn: async ({ email, lang }: { email: string; lang: SupportedLanguage }) => {
      return await sendLinkVerificationAPI({
        supportCaseId,
        kind: 'email',
        email,
        lang,
      });
    },
    onSuccess: (response) => {
      if (response?.status) {
        toast({
          description: L.applicant.comms.sent,
          variant: 'success',
        });
        setEmail('');
      }
    },
    onError: () => {
      toast({
        description: L.mfa_link.failed_to_send,
        variant: 'error',
      });
    },
  });

  const handleSendVerification = () => {
    // Basic email validation
    const emailPattern = /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    if (!emailPattern.test(email)) {
      toast({
        description: L.mfa_link.invalid_email_address,
        variant: 'error',
      });
      return;
    }

    sendVerificationMutation.mutate({
      email,
      lang: selectedLang.value as SupportedLanguage,
    });
  };

  return (
    <div className="flex flex-col space-y-4">
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-0">{L.mfa_link.verified_contacts}</h3>
      {verified && verified.length > 0 && (
        <div>
          <ul className="list-disc pl-3 space-y-1 mb-0">
            {verified.map((v) => (
              <li key={v} className="text-sm text-gray-700">
                {v}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <Input
          id="email"
          label={L.mfa_link.enter_an_email_to_verify}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          description={L.mfa_link.we_will_send_an_email}
          className="w-full"
        />
      </div>

      <div>
        <Select
          label={L.mfa_link.select_a_language}
          description=""
          value={selectedLang}
          onChange={(value) => setSelectedLang(value)}
          options={languageOptions}
          className="w-full"
          searchable={true}
        />
      </div>

      <Button
        onClick={handleSendVerification}
        className="w-full"
        disabled={!email.trim() || sendVerificationMutation.isPending}
      >
        {L.mfa_link.send_verification_link}
      </Button>
    </div>
  );
};
